import type { AppModel } from '@/models/AppModel';
import type { IChipModel } from '@/interfaces/models';
import type { LogicGraph } from '@/models/logic/LogicGraph';
import { downloadJson } from '@/utils/fileIO';

interface DebugNodeInfo {
  id: string;
  type: string;
  output: boolean;
  state: unknown;
  inputEdges: { from: string; fromType: string; fromOutput: boolean }[];
}

interface DebugGraphInfo {
  nodeCount: number;
  edgeCount: number;
  nodes: DebugNodeInfo[];
  edges: { id: string; source: string; target: string; meta?: unknown }[];
}

interface DebugTileInfo {
  id: string;
  tileType: string;
  location: { x: number; y: number };
  rotation: number;
  logicNodeIds?: Partial<Record<string, string>>;
  partOfChip?: string;
  state: unknown;
  connections?: Record<string, boolean>;
}

interface DebugChipInstanceInfo {
  id: string;
  chipId: string;
  chipName: string | undefined;
  originPoint: { x: number; y: number };
  templateToInstanceIdMap: Record<string, string>;
  logicGraph: DebugGraphInfo;
}

interface DebugInterfaceLinkInfo {
  id: string;
  label: string;
  color: string;
  type: number;
  logicTileId?: string;
  structureTileId?: string;
}

interface DebugChipInfo {
  id: string;
  name: string;
  description: string;
  color: string;
  logicCircuit: {
    tiles: DebugTileInfo[];
    connections: unknown;
  };
  structureCircuit: {
    tiles: DebugTileInfo[];
    connections: unknown;
    originLocation: { x: number; y: number };
  };
  interfaceLinks: DebugInterfaceLinkInfo[];
  interfaceNodeMap: Record<string, Partial<Record<string, string>>>;
  templateGraph: DebugGraphInfo | null;
  chipInstances: DebugChipInstanceInfo[];
  activeLogicGraph: DebugGraphInfo;
}

function serializeGraph(graph: LogicGraph): DebugGraphInfo {
  const nodes: DebugNodeInfo[] = [];
  for (const [id, node] of graph.nodes) {
    const inputEdges: DebugNodeInfo['inputEdges'] = [];
    // Find edges pointing to this node
    for (const edge of graph.edges.values()) {
      if (edge.targetNodeId === id) {
        const sourceNode = graph.nodes.get(edge.sourceNodeId);
        inputEdges.push({
          from: graph.nodeIdText(edge.sourceNodeId),
          fromType: sourceNode?.type ?? 'unknown',
          fromOutput: sourceNode?.output ?? false,
        });
      }
    }
    nodes.push({
      id: node.idText,
      type: node.type,
      output: node.output,
      state: node.state,
      inputEdges,
    });
  }

  const edges = [...graph.edges.values()].map((edge) => graph.edgeDto(edge)).map((e) => ({
    id: e.id,
    source: e.sourceNodeId,
    target: e.targetNodeId,
    meta: e.meta,
  }));

  return { nodeCount: graph.nodes.size, edgeCount: graph.edges.size, nodes, edges };
}

function serializeTile(tile: any): DebugTileInfo {
  return {
    id: tile.id,
    tileType: tile.tileType,
    location: { x: tile.location.x, y: tile.location.y },
    rotation: tile.meta?.rotation ?? 0,
    logicNodeIds: tile.dto.logicNodeIds,
    partOfChip: tile.meta?.partOfChip,
    state: JSON.parse(JSON.stringify(tile.dto.state ?? null)),
    connections: tile.meta?.connections ? { ...tile.meta.connections } : undefined,
  };
}

function serializeChip(chip: IChipModel): DebugChipInfo {
  const logicTiles = chip.logicCircuit.circuitModel.tileManagerModel.tiles;
  const structureTiles = chip.structureCircuit.circuitModel.tileManagerModel.tiles;

  const chipInstances: DebugChipInstanceInfo[] = [];
  for (const [, instance] of chip.logicCircuit.chipInstances) {
    chipInstances.push({
      id: instance.id,
      chipId: instance.chipId,
      chipName: instance.chip?.name,
      originPoint: { x: instance.originPoint.x, y: instance.originPoint.y },
      templateToInstanceIdMap: instance.templateToInstanceIdMap.toDto(instance.logicGraph, instance.chip?.templateGraph) ?? {},
      logicGraph: serializeGraph(instance.logicGraph as LogicGraph),
    });
  }

  const interfaceNodeMap: Record<string, Partial<Record<string, string>>> = {};
  for (const [linkId, nodeIds] of chip.interfaceNodeMap) {
    interfaceNodeMap[linkId] = Object.fromEntries(Object.entries(nodeIds).map(([side, id]) => [side, chip.templateGraph?.nodeIdText(id) ?? chip.logicCircuit.logicGraph.nodeIdText(id)]));
  }

  return {
    id: chip.id,
    name: chip.name,
    description: chip.description,
    color: chip.color,
    logicCircuit: {
      tiles: logicTiles.map(serializeTile),
      connections: chip.logicCircuit.circuitModel.connectionModel?.dto ?? null,
    },
    structureCircuit: {
      tiles: structureTiles.map(serializeTile),
      connections: chip.structureCircuit.circuitModel.connectionModel?.dto ?? null,
      originLocation: {
        x: chip.structureCircuit.originLocation.x,
        y: chip.structureCircuit.originLocation.y,
      },
    },
    interfaceLinks: chip.interfaceLinks.map((link) => ({
      id: link.id,
      label: link.label,
      color: link.color,
      type: link.type as number,
      logicTileId: link.tiles?.ChipLogic?.id,
      structureTileId: link.tiles?.ChipStructure?.id,
    })),
    interfaceNodeMap,
    templateGraph: chip.templateGraph
      ? serializeGraph(chip.templateGraph as LogicGraph)
      : null,
    chipInstances,
    activeLogicGraph: serializeGraph(chip.logicCircuit.logicGraph),
  };
}

export function debugExportChip(chip: IChipModel): DebugChipInfo {
  return serializeChip(chip);
}

export function debugExportAll(appModel: AppModel): object {
  const context = appModel.context;
  if (!context) {
    return { error: 'No context — create or load a project first.' };
  }

  const chips: DebugChipInfo[] = [];
  for (const chip of context.chipStore.chips) {
    chips.push(serializeChip(chip));
  }

  return {
    exportedAt: new Date().toISOString(),
    mainChipId: context.mainChip?.id,
    currentChipId: context.editor?.currentChip?.id,
    chips,
  };
}

/**
 * Call from browser console:
 *   window.__debugExport()          — logs + copies to clipboard
 *   window.__debugExport('download') — downloads as JSON file
 */
export function installDebugExport(appModel: AppModel) {
  (window as any).__debugExport = async (mode?: 'download' | 'clipboard') => {
    const data = debugExportAll(appModel);

    if (mode === 'download') {
      await downloadJson(data, `digitiles-debug-${Date.now()}.json`);
      console.log('Downloaded debug export.');
    } else {
      const json = JSON.stringify(data, null, 2);
      navigator.clipboard.writeText(json).then(
        () => console.log('Debug export copied to clipboard.'),
        () => console.log('Clipboard failed. JSON logged below.'),
      );
      console.log(json);
    }

    return data;
  };
}
